import React from "react";
import { Box, Typography, styled } from "@mui/material";
import type { SimpleNamedItem } from "../../../types";
import { PathDefinitionTooltip } from "../../PathDefinitionTooltip";

interface RouteCardProps {
  title: string;
  subtitle: string;
  items: SimpleNamedItem[];
  headerColor: string;
  /** Message shown in the card body when there are no items. */
  emptyMessage?: string;
}

const Card = styled(Box, {
  label: "RouteCard",
})(() => ({
  width: 400,
  display: "flex",
  flexDirection: "column",
  borderRadius: 24,
  overflow: "hidden",
}));

const CardHeader = styled(Box, {
  label: "RouteCardHeader",
  shouldForwardProp: (prop) => prop !== "bgColor",
})<{ bgColor: string }>(({ bgColor, theme }) => ({
  backgroundColor: bgColor,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  color: "#ffffff",
  padding: theme.spacing(2),
}));

const CardBody = styled(Box, {
  label: "RouteCardBody",
  shouldForwardProp: (prop) => prop !== "isEmpty",
})<{ isEmpty?: boolean }>(({ isEmpty, theme }) => ({
  backgroundColor: "#ffffff",
  height: 256,
  display: "flex",
  flexDirection: "column",
  gap: 4,
  alignItems: isEmpty ? "center" : "flex-start",
  padding: theme.spacing(2, 3),
}));

const RouteItem = styled(Box, {
  label: "RouteItem",
})(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  width: 251,
}));

const RouteItemIcon = styled("img", {
  label: "RouteItemIcon",
})(() => ({
  width: 32,
  height: 32,
  objectFit: "contain",
  flexShrink: 0,
}));

const HeaderTitle = styled(Typography, {
  label: "RouteCardHeaderTitle",
})(() => ({
  fontFamily: "Proxima Nova",
  fontWeight: 700,
  fontSize: "24px",
  lineHeight: "36px",
}));

const HeaderSubtitle = styled(Typography, {
  label: "RouteCardHeaderSubtitle",
})(() => ({
  fontFamily: "Proxima Nova",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "18px",
}));

// Shared by the empty-state message and each route item's name.
const RouteItemName = styled(Typography, {
  label: "RouteCardItemName",
})(() => ({
  fontFamily: "Proxima Nova",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "28px",
  color: "#1c252e",
  textAlign: "center",
}));

const OrSeparator = styled(Typography, {
  label: "RouteCardOrSeparator",
})(() => ({
  fontFamily: "Proxima Nova",
  fontSize: "14px",
  lineHeight: "22px",
  color: "#637381",
  textAlign: "center",
  width: "100%",
}));

export const RouteCard = ({
  title,
  subtitle,
  items,
  headerColor,
  emptyMessage,
}: RouteCardProps) => {
  const isEmpty = items.length === 0;
  return (
    <Card>
      <CardHeader bgColor={headerColor}>
        <HeaderTitle>{title}</HeaderTitle>
        <HeaderSubtitle>{subtitle}</HeaderSubtitle>
      </CardHeader>

      <CardBody isEmpty={isEmpty}>
        {isEmpty && emptyMessage ? (
          <RouteItemName sx={{ width: "100%" }}>{emptyMessage}</RouteItemName>
        ) : (
          items.map((item, index) => (
            <React.Fragment key={item.id}>
              {index > 0 && <OrSeparator>Or</OrSeparator>}
              <RouteItem>
                {item.icon?.url && (
                  <RouteItemIcon src={item.icon.url} alt={item.name} />
                )}
                <PathDefinitionTooltip item={item}>
                  <RouteItemName>{item.name}</RouteItemName>
                </PathDefinitionTooltip>
              </RouteItem>
            </React.Fragment>
          ))
        )}
      </CardBody>
    </Card>
  );
};
